import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Outlet } from 'react-router-dom';

const CorporateContext = createContext<{
  isCorporate: boolean;
  setIsCorporate: (value: boolean) => void;
}>({
  isCorporate: false,
  setIsCorporate: () => {}
});

export const CorporateContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [isCorporate, setIsCorporate] = useState(false);

  const value = useMemo(
    () => ({
      isCorporate,
      setIsCorporate
    }),
    [isCorporate, setIsCorporate]
  );

  return (
    <CorporateContext.Provider value={value}>
      {children}
    </CorporateContext.Provider>
  );
};

/**
 * Custom hook to use the CorporateContext directly.
 *
 * ```js
 *  const { isCorporate, setIsCorporate } = useCorporateContext();
 *
 *  console.log( isCorporate ? 'Show corporate info' : 'Show private info' )
 * ```
 */
export const useCorporateContext = () => {
  return useContext(CorporateContext);
};

/**
 * Renders child routes and sets the given isCorporate value in CorporateContext.
 */
export const CorporateContextPath = ({
  isCorporate
}: {
  isCorporate: boolean;
}) => {
  const { setIsCorporate } = useCorporateContext();

  useEffect(() => {
    setIsCorporate(isCorporate);
  }, [isCorporate, setIsCorporate]);

  return <Outlet />;
};
